/**
 * Accounting periods: AndroMoney allows the "month" to start on an arbitrary
 * day (e.g. day 25, payday). All monthly views and budgets use this range
 * instead of the calendar month.
 */

export interface PeriodRange {
  /** Inclusive start (ISO). */
  fromISO: string;
  /** Exclusive end (ISO). */
  toISO: string;
}

/**
 * Range of the accounting month containing `date`, starting on `monthStartDay`.
 * With monthStartDay=1 it is equivalent to the calendar month. Days 29-31 are handled with
 * safe clamping (a start no later than the 28th — the DB limits it to 1..28).
 */
export function accountingMonthRange(date: Date, monthStartDay = 1): PeriodRange {
  const startDay = Math.min(Math.max(monthStartDay, 1), 28);
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();

  // If the start day has not arrived yet, the period started last month.
  const startMonth = d >= startDay ? m : m - 1;
  const from = new Date(y, startMonth, startDay, 0, 0, 0, 0);
  const to = new Date(y, startMonth + 1, startDay, 0, 0, 0, 0);
  return { fromISO: from.toISOString(), toISO: to.toISOString() };
}

/** Shifts a monthly range by N periods (for the month-switcher). */
export function shiftAccountingMonth(
  range: PeriodRange,
  offset: number,
  monthStartDay = 1,
): PeriodRange {
  const from = new Date(range.fromISO);
  const shifted = new Date(
    from.getFullYear(),
    from.getMonth() + offset,
    from.getDate(),
    0,
    0,
    0,
    0,
  );
  return accountingMonthRange(shifted, monthStartDay);
}

/** Human-readable period label: the month in which most of the range falls. */
export function accountingMonthLabelDate(range: PeriodRange): Date {
  const from = new Date(range.fromISO);
  // With a start day >15, most of the period falls in the following month.
  return from.getDate() > 15
    ? new Date(from.getFullYear(), from.getMonth() + 1, 1)
    : new Date(from.getFullYear(), from.getMonth(), 1);
}

/** Stable YYYY-MM-DD key for the local day of an ISO date (daily grouping). */
export function localDayKey(iso: string): string {
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}
